const USER_TENURE_OPTIONS = [
    { value: '0-29-days', label: '0 - 29 days', min: 0, max: 29 },
    { value: '30-59-days', label: '30 - 59 days', min: 30, max: 59 },
    { value: '60-89-days', label: '60 - 89 days', min: 60, max: 89 },
    { value: '90-plus-days', label: '90+ days', min: 90 },
    { value: '180-plus-days', label: '180+ days', min: 180 },
    { value: '365-plus-days', label: '365+ days', min: 365 }
];

const USER_TENURE_SEGMENT_MIN = {
    'more-than-30-days': 30,
    'more-than-60-days': 60,
    'more-than-90-days': 90,
    'more-than-180-days': 180,
    'more-than-365-days': 365
};

const OVERDRAFT_OPTIONS = [
    { value: 'less-than-1', label: '0 overdraft / NSF instances', max: 0 },
    { value: 'less-than-2', label: 'Up to 1 overdraft / NSF instance', max: 1 },
    { value: 'less-than-3', label: 'Up to 2 overdraft / NSF instances', max: 2 },
    { value: 'less-than-4', label: 'Up to 3 overdraft / NSF instances', max: 3 },
    { value: 'less-than-5', label: 'Up to 4 overdraft / NSF instances', max: 4 },
    { value: 'less-than-6', label: 'Up to 5 overdraft / NSF instances', max: 5 }
];

const ACH_OPTIONS = [
    { value: 'less-than-1', label: '0 ACH return / chargebacks', max: 0 },
    { value: 'less-than-2', label: 'Up to 1 ACH return / chargeback', max: 1 },
    { value: 'less-than-3', label: 'Up to 2 ACH returns / chargebacks', max: 2 },
    { value: 'less-than-5', label: 'Up to 4 ACH returns / chargebacks', max: 4 }
];

const COUNT_SEGMENT_MAX = {
    'less-than-1': 0,
    'less-than-2': 1,
    'less-than-3': 2,
    'less-than-4': 3,
    'less-than-5': 4,
    'less-than-6': 5
};

const BALANCE_OPTIONS = [
    { value: '0-to-499', label: '0 to $499', max: 499 },
    { value: '500-to-999', label: '500 to $999', max: 999 },
    { value: '1000-to-2499', label: '1000 to $2499', max: 2499 },
    { value: '2500-to-4999', label: '2500 to $4999', max: 4999 },
    { value: '5000-plus', label: '5000+', min: 5000 }
];

const BALANCE_SEGMENT_MAX = {
    '0-to-499': 499,
    '500-to-999': 999,
    '1000-to-2499': 2499,
    '2500-to-4999': 4999,
    '5000-plus': 5000
};

const isSegmentationActive = (segmentationEnabled, key, mode) => (mode === 'open' ? false : segmentationEnabled?.[key]);

export function getUserTenureOptions(segmentPolicy = {}, segmentationEnabled = {}) {
    const blockOutOfSegmentation = segmentPolicy.blockOutOfSegmentation === true;
    const isFieldEnabled = isSegmentationActive(segmentationEnabled, 'bankingTenure', segmentPolicy.mode);

    // If block is off OR field is not enabled, show all options
    if (!blockOutOfSegmentation || !isFieldEnabled) {
        return USER_TENURE_OPTIONS;
    }

    // If block is on AND field is enabled, filter based on segmentation choice
    const minAllowed = USER_TENURE_SEGMENT_MIN[segmentPolicy.bankingTenure] ?? 0;
    // Include options >= minAllowed (includes the chosen option)
    return USER_TENURE_OPTIONS.filter((option) => (option.min ?? 0) >= minAllowed);
}

export function getOverdraftOptions(segmentPolicy = {}, segmentationEnabled = {}) {
    const blockOutOfSegmentation = segmentPolicy.blockOutOfSegmentation === true;
    const isFieldEnabled = isSegmentationActive(segmentationEnabled, 'overdraftHistory', segmentPolicy.mode);

    // If block is off OR field is not enabled, show all options
    if (!blockOutOfSegmentation || !isFieldEnabled) {
        return OVERDRAFT_OPTIONS;
    }

    // If block is on AND field is enabled, filter based on segmentation choice
    const maxAllowed = COUNT_SEGMENT_MAX[segmentPolicy.overdraftHistory];
    if (maxAllowed === undefined) {
        return OVERDRAFT_OPTIONS;
    }

    // Include options <= maxAllowed (includes the chosen option)
    return OVERDRAFT_OPTIONS.filter((option) => option.max <= maxAllowed);
}

export function getAchOptions(segmentPolicy = {}, segmentationEnabled = {}) {
    const blockOutOfSegmentation = segmentPolicy.blockOutOfSegmentation === true;
    const isFieldEnabled = isSegmentationActive(segmentationEnabled, 'returnedACHs', segmentPolicy.mode);

    // If block is off OR field is not enabled, show all options
    if (!blockOutOfSegmentation || !isFieldEnabled) {
        return ACH_OPTIONS;
    }

    // If block is on AND field is enabled, filter based on segmentation choice
    const maxAllowed = COUNT_SEGMENT_MAX[segmentPolicy.returnedACHs];
    if (maxAllowed === undefined) {
        return ACH_OPTIONS;
    }

    // Include options <= maxAllowed (includes the chosen option)
    return ACH_OPTIONS.filter((option) => option.max <= maxAllowed);
}

export function getBalanceOptions(segmentPolicy = {}, segmentationEnabled = {}) {
    const blockOutOfSegmentation = segmentPolicy.blockOutOfSegmentation === true;
    const isFieldEnabled = isSegmentationActive(segmentationEnabled, 'avgDailyBalance', segmentPolicy.mode);

    // If block is off OR field is not enabled, show all options
    if (!blockOutOfSegmentation || !isFieldEnabled) {
        return BALANCE_OPTIONS;
    }

    // If block is on AND field is enabled, filter based on segmentation choice
    const maxAllowed = BALANCE_SEGMENT_MAX[segmentPolicy.avgDailyBalance];
    if (maxAllowed === undefined) {
        return BALANCE_OPTIONS;
    }

    // Include options <= maxAllowed (includes the chosen option)
    return BALANCE_OPTIONS.filter((option) => option.max <= maxAllowed);
}

export function sanitizeBalanceRules(balanceRules = {}, segmentPolicy = {}, segmentationEnabled = {}) {
    const userTenureOptionValues = new Set(getUserTenureOptions(segmentPolicy, segmentationEnabled).map((option) => option.value));
    const overdraftOptionValues = new Set(getOverdraftOptions(segmentPolicy, segmentationEnabled).map((option) => option.value));
    const achOptionValues = new Set(getAchOptions(segmentPolicy, segmentationEnabled).map((option) => option.value));
    const balanceOptionValues = new Set(getBalanceOptions(segmentPolicy, segmentationEnabled).map((option) => option.value));

    const sanitizeRuleArray = (rules = [], allowedValues) =>
        (rules || [])
            .filter((rule) => allowedValues.has(rule.condition))
            .map((rule) => ({
                ...rule,
                type: 'exactly',
                value: Number(rule.value ?? rule.deduction ?? 50)
            }));

    return {
        userTenureRules: sanitizeRuleArray(balanceRules.userTenureRules, userTenureOptionValues),
        overdraftRules: sanitizeRuleArray(balanceRules.overdraftRules, overdraftOptionValues),
        achRules: sanitizeRuleArray(balanceRules.achRules, achOptionValues),
        employmentTenureRules: (balanceRules.employmentTenureRules || []).map((rule) => ({
            ...rule,
            type: 'exactly',
            value: Number(rule.value ?? 50)
        })),
        employmentRules: (balanceRules.employmentRules || []).map((rule) => ({
            ...rule,
            type: 'exactly',
            value: Number(rule.value ?? rule.deduction ?? 50)
        })),
        balanceRules: sanitizeRuleArray(balanceRules.balanceRules, balanceOptionValues)
    };
}
